import React, {
  useMemo,
} from 'react';
import {
  Label, Form, Input, Grid, Icon, Checkbox,
} from 'semantic-ui-react';
import { MultiLineParagraph } from 'components/lib/UI';
import styled from 'styled-components';
import moment from 'moment';

const AssessmentContainer = styled.div`
  padding: 15px;
  background-color: #fff;
  ${({ floating }) => (floating ? `
    border-radius: 5px;
  ` : `
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
  `)}

  border: 1.5px solid purple;
  margin: 0 -1px;
  &:not(:last-of-type) {
    margin-bottom: 20px;
  }
  ${({ noShadow }) => !noShadow && 'box-shadow:0px 10px 10px rgba(0,0,0,0.1);'}
  ${({ isPDF }) => (isPDF && `
    margin: 20px;
  `)}
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  h3 {
    display: block;
    margin: ${({ noMargin }) => (noMargin ? '0' : '0 0 0')};
  }
  > span {
    display: block;
    color: gray;
    text-decoration-line: underline;
    font-size: 12px;
  }
  margin-bottom: 10px;
`;

export const IdeaAssessmentConflictOfInterestBox = ({
  idea, assessment, updateAssessment, noShadow, style, className, isPDF,
}) => {
  const isSubmitted = assessment?.isSubmitted;
  const { conflictOfInterest = {} } = (assessment || {});
  const { hasConflictOfInterest, notes } = conflictOfInterest;

  if (idea?.isAssessor) {
    return (
      <AssessmentContainer noShadow={noShadow} style={style} className={className} floating isPDF={isPDF}>
        <TitleContainer noMargin>
          <h3>
            Conflict of interest
          </h3>
          {isSubmitted && <Label size='small' color='green'>Submitted</Label>}
        </TitleContainer>
        <Checkbox
          checked={hasConflictOfInterest}
          label='I would like to declare a conflict of interest for this assessment'
          onChange={() => updateAssessment(!hasConflictOfInterest, ['conflictOfInterest', 'hasConflictOfInterest'])}
          disabled={isSubmitted}
        />
        {hasConflictOfInterest
          ? (
            <Form style={{ marginTop: 10 }}>
              <Form.TextArea
                placeholder='Please provide any further context or information..'
                disabled={isSubmitted}
                value={notes}
                onChange={(e, { value }) => updateAssessment(value, ['conflictOfInterest', 'notes'])}
              />
            </Form>
          ) : null
        }
      </AssessmentContainer>
    );
  }
  return null;
};

const IdeaAssessmentBox = ({
  idea,
  section: propSection,
  assessment,
  usingAssessmentTemplate,
  updateAssessment,
  assessmentSavedAt = {},
  noShadow,
  style,
  className,
  // If the idea is being rendered in PDF format, we need to render the assessment box differently for the PDF formatting
  // We still include the text entry boxes so the users can see their options for scoring and note taking
  isPDF,
}) => {
  const section = useMemo(() => (
    usingAssessmentTemplate?.body
    ? usingAssessmentTemplate.body.find((section) => section.id === propSection.id)
    : propSection
  ), [propSection, usingAssessmentTemplate?.body]);

  const isAssessor = idea?.isAssessor;
  if (!isAssessor || !section?.assessment?.availableScore) return null;
  const current = { ...assessment?.assessment && assessment.assessment[section.id] };
  const { assessmentClosed } = (idea?.ideaTemplate || {});
  return (
    <AssessmentContainer noShadow={noShadow} style={style} className={className} isPDF={isPDF}>
      <TitleContainer>
        <h3>
          Assessment for {section.name}
        </h3>
        {assessment?.isSubmitted && <Label size='small' color='green'>Submitted</Label>}
        {!assessment?.isSubmitted && assessmentSavedAt[section.id]
          ? (
            <span><Icon name='save' style={{ marginRight: 5 }} />Autosaved at {moment(assessmentSavedAt[section.id]).format('HH:mm:ss')}</span>
          ) : null
        }
      </TitleContainer>
      {usingAssessmentTemplate
        ? (
          <span style={{ display: 'block', marginBottom: 10 }}>Your assessment scores and notes are shared between all merged ideas, and you only have to submit once.</span>
        ) : null
      }
      {section.assessment.notes && <MultiLineParagraph style={{ backgroundColor: '#e6f2ff', padding: 3 }}>{section.assessment.notes}</MultiLineParagraph>}
      <Grid stackable>
        <Grid.Column computer={5}>
          <p style={{ fontSize: 12, fontWeight: 'bold' }}>Your score</p>
          <Input
            disabled={assessment?.isSubmitted || assessmentClosed}
            label={{ basic: true, content: `/${section.assessment.availableScore}` }}
            labelPosition='right'
            fluid
            value={current.score}
            onChange={(e) => updateAssessment(parseInt(e.target.value, 10), ['assessment', section.id, 'score'])}
            type='number'
            min='1'
            max={String(section.assessment.availableScore)}
          />
        </Grid.Column>
        <Grid.Column computer={11}>
          <p style={{ fontSize: 12, fontWeight: 'bold' }}>Assessment notes</p>
          <Form><Form.TextArea disabled={assessment?.isSubmitted || assessmentClosed} placeholder='Justification or notes...' value={current.notes || ''} onChange={(e) => updateAssessment(e.target.value, ['assessment', section.id, 'notes'])} /></Form>
        </Grid.Column>
      </Grid>
    </AssessmentContainer>
  );
};

export default IdeaAssessmentBox;
